/**
 * Schedule Page - View and manage scheduled lessons
 * 
 * Per Feb 2026 wireframes:
 * - "Scheduled Lessons" header with "+ Schedule Lesson" button
 * - Table: Date | Time | Time Zone | Join | Cancel | Reschedule
 * - Voice scheduling hint at bottom
 * - BookingModal for visual slot selection (book + reschedule)
 * 
 * Design: Apple-inspired dark aesthetic
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Plus, Video, X, Loader2, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useViewAs } from '@/contexts/ViewAsContext';
import { useSelectedChild } from '@/contexts/ChildContext';
import { supabase } from '@/lib/supabase/client';
import { glassStyles, colors } from '@/lib/design';
import { useTranslation } from '@/lib/i18n';
import { BookingModal } from '@/components/scheduling/BookingModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useParentStatus } from '@/hooks/useParentStatus';
import { useModalHistory } from '@/hooks/useModalHistory';

interface ScheduledClass {
  id: string;
  status: string;
  zoom_join_url: string | null;
  slot_id: string | null;
  child: {
    id: string;
    name: string;
  } | null;
  slot: {
    id: string;
    start_time: string;
    end_time: string;
    slot_type: string;
    instructor: {
      name: string;
    } | null;
  } | null;
}

interface BookingModalState {
  mode: 'book' | 'reschedule';
  childId: string;
  childName?: string;
  existingBookingId?: string;
  existingSlotId?: string;
}

export const SchedulePage: React.FC = () => {
  const { profile } = useAuth();
  const { effectiveParentId } = useViewAs();
  const { t } = useTranslation();
  const { selectedChild, children, selectedChildId, setSelectedChildId, isMultiChild, isLoading: childrenLoading } = useSelectedChild();
  const [classes, setClasses] = useState<ScheduledClass[]>([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [bookingModal, setBookingModal] = useState<BookingModalState | null>(null);

  // Use effectiveParentId for data queries (supports admin preview via ?viewAs=)
  const parentId = effectiveParentId || profile?.id;

  // Browser back button closes modal instead of navigating away
  useModalHistory(!!bookingModal, () => setBookingModal(null), 'booking');

  // Get parent/trial status for the selected child
  const parentStatus = useParentStatus({ childId: selectedChildId ?? undefined, profileId: parentId });

  const loading = childrenLoading || classesLoading;

  // Load classes for parent
  useEffect(() => {
    const loadData = async () => {
      if (!parentId) return;

      setClassesLoading(true);

      try {
        const { data, error } = await supabase
          .from('scheduled_classes')
          .select(`
            id,
            status,
            zoom_join_url,
            slot_id,
            child:children!scheduled_classes_child_id_fkey (
              id,
              name
            ),
            slot:class_slots (
              id,
              start_time,
              end_time,
              slot_type,
              instructor:instructors (
                name
              )
            )
          `)
          .eq('parent_id', parentId)
          .in('status', ['scheduled', 'confirmed']);

        if (!error && data) {
          const now = new Date().toISOString();
          const upcoming = (data as ScheduledClass[])
            .filter(c => c.slot && c.slot.start_time >= now)
            .sort((a, b) => {
              const aTime = a.slot?.start_time || '';
              const bTime = b.slot?.start_time || '';
              return aTime.localeCompare(bTime);
            });
          setClasses(upcoming);
        } else if (error) {
          console.error('Error loading classes:', error);
        }
      } finally {
        setClassesLoading(false);
      }
    };
    
    loadData();
  }, [parentId]);

  // Refresh classes only (for after booking/cancellation)
  const loadClasses = async () => {
    if (!parentId) return;

    try {
      const { data, error } = await supabase
        .from('scheduled_classes')
        .select(`
          id,
          status,
          zoom_join_url,
          slot_id,
          child:children!scheduled_classes_child_id_fkey (
            id,
            name
          ),
          slot:class_slots (
            id,
            start_time,
            end_time,
            slot_type,
            instructor:instructors (
              name
            )
          )
        `)
        .eq('parent_id', parentId)
        .in('status', ['scheduled', 'confirmed']);

      if (error) {
        console.error('Error loading classes:', error);
        return;
      }

      // Filter and sort client-side since start_time is on the joined slot
      const now = new Date().toISOString();
      const filtered = (data || [])
        .filter((cls: ScheduledClass) => cls.slot?.start_time && cls.slot.start_time >= now)
        .sort((a: ScheduledClass, b: ScheduledClass) => {
          const aTime = a.slot?.start_time || '';
          const bTime = b.slot?.start_time || '';
          return aTime.localeCompare(bTime);
        });

      setClasses(filtered as ScheduledClass[]);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCancelConfirm = async (classId: string) => {
    setCancellingId(classId);
    try {
      const session = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scheduling-book`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session?.access_token}`,
          },
          body: JSON.stringify({
            action: 'cancel',
            bookingId: classId, // camelCase to match edge function interface
          }),
        }
      );

      if (response.ok) {
        setClasses(prev => prev.filter(c => c.id !== classId));
        toast.success(t('Lesson Cancelled'));
      } else {
        const data = await response.json();
        toast.error(data.error || t('Failed to cancel'));
      }
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error(t('Failed to cancel lesson'));
    } finally {
      setCancellingId(null);
      setCancelConfirmId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getTimezone = () => {
    const date = new Date();
    const tzAbbr = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    return tzAbbr || Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#5C2D91] mx-auto mb-3" />
          <p className="text-[#636366]">{t('Loading schedule...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Trial lesson limit warning */}
      {parentStatus.isInTrial && !parentStatus.canScheduleLesson && (
        <div className="mb-6 p-4 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/20 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#FF3B30] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-[#FF3B30]">{t('Trial lesson limit reached')}</p>
            <p className="text-sm text-[#636366] mt-1">
              {t("You've used all")} {parentStatus.trialLessonsMax} {t('trial lessons. Subscribe to continue scheduling lessons with Turn.')}
            </p>
            <Link 
              to="/settings" 
              className="inline-block mt-2 text-sm text-[#5C2D91] hover:underline font-medium"
            >
              {t('Manage Subscription')}
            </Link>
          </div>
        </div>
      )}
      
      {/* Trial lessons remaining indicator */}
      {parentStatus.isInTrial && parentStatus.canScheduleLesson && (
        <div className="mb-6 p-4 rounded-xl bg-[#FFD60A]/10 border border-[#FFD60A]/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-sm text-[#1C1C1E]">
              <strong className="text-[#FFD60A]">{parentStatus.trialLessonsRemaining}</strong> {t('of')} {parentStatus.trialLessonsMax} {t('trial lessons remaining')}
            </span>
          </div>
          {parentStatus.trialDaysRemaining !== null && (
            <span className="text-xs text-[#8E8E93]">
              {parentStatus.trialDaysRemaining} {t('days left in trial')}
            </span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#5C2D91]/15 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-[#5C2D91]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#1C1C1E]">{t('Scheduled Lessons')}</h1>
            {isMultiChild && (
              <div className="flex items-center gap-2 mt-1">
                {children.map(child => (
                  <button
                    key={child.id}
                    onClick={() => setSelectedChildId(child.id)}
                    className={`px-3 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      selectedChildId === child.id
                        ? 'bg-[#5C2D91] text-white'
                        : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.05]'
                    }`}
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <button
          onClick={() => {
            // Check trial lesson limit
            if (parentStatus.isInTrial && !parentStatus.canScheduleLesson) {
              toast.error(t("You've used all your trial lessons. Please subscribe to continue."));
              return;
            }
            
            // Use selected child from context, otherwise prompt user
            if (selectedChild) {
              setBookingModal({
                mode: 'book',
                childId: selectedChild.id,
                childName: selectedChild.name,
              });
            } else {
              toast.error(t('Please add a child to your account first.'));
            }
          }}
          disabled={parentStatus.isInTrial && !parentStatus.canScheduleLesson}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#5C2D91] hover:brightness-110 disabled:bg-black/[0.05] disabled:text-[#C7C7CC] disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">{t('Schedule Lesson')}</span>
          <span className="sm:hidden">{t('New')}</span>
        </button>
      </div>

      {/* Empty state */}
      {classes.length === 0 ? (
        <div className="text-center py-16 rounded-2xl" style={glassStyles.card}>
          <div className="w-16 h-16 bg-black/[0.03] rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-[#C7C7CC]" />
          </div>
          <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">{t('No upcoming lessons')}</h3>
          <p className="text-[#8E8E93] max-w-sm mx-auto">
            {t('Schedule a lesson with Addison to start your Turn journey.')}
          </p>
        </div>
      ) : (
        /* Classes table */
        <div className="rounded-2xl overflow-hidden" style={glassStyles.card}>
          {/* Table header - desktop */}
          <div className="hidden sm:grid sm:grid-cols-5 gap-4 px-6 py-3 bg-black/[0.03] border-b border-black/[0.06] text-sm font-medium text-[#8E8E93]">
            <div>{t('Date')}</div>
            <div>{t('Time')}</div>
            <div>{t('Time Zone')}</div>
            <div className="col-span-2 text-right">{t('Actions')}</div>
          </div>

          {/* Table rows */}
          <div className="divide-y divide-black/[0.06]">
            {classes.map((cls) => (
              <div key={cls.id} className="px-6 py-4">
                {/* Mobile layout */}
                <div className="sm:hidden space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-[#1C1C1E]">{cls.slot ? formatDate(cls.slot.start_time) : t('Time pending')}</p>
                      <p className="text-sm text-[#636366]">
                        {cls.slot ? `${formatTime(cls.slot.start_time)} ${getTimezone()}` : ''}
                      </p>
                      {cls.child && (
                        <p className="text-sm text-[#5C2D91] mt-1">{t('for')} {cls.child.name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {cls.zoom_join_url ? (
                      <a
                        href={cls.zoom_join_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-[#5C2D91] hover:brightness-110 text-white rounded-xl font-medium transition-all"
                      >
                        <Video className="w-4 h-4" />
                        {t('Join')}
                      </a>
                    ) : (
                      <button
                        disabled
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-black/[0.03] text-[#8E8E93] rounded-xl font-medium cursor-not-allowed"
                        title={t('Zoom link will be available closer to the lesson')}
                      >
                        <Clock className="w-4 h-4" />
                        {t('Join (soon)')}
                      </button>
                    )}
                    <button
                      onClick={() => setCancelConfirmId(cls.id)}
                      disabled={cancellingId === cls.id}
                      className="px-4 py-2.5 min-h-[44px] border border-black/[0.08] hover:border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 text-[#636366] hover:text-[#FF3B30] rounded-xl font-medium transition-all disabled:opacity-50"
                    >
                      {cancellingId === cls.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('Cancel')
                      )}
                    </button>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden sm:grid sm:grid-cols-5 gap-4 items-center">
                  <div>
                    <p className="font-medium text-[#1C1C1E]">{cls.slot ? formatDate(cls.slot.start_time) : t('Time pending')}</p>
                    {cls.child && (
                      <p className="text-sm text-[#5C2D91]">{t('for')} {cls.child.name}</p>
                    )}
                  </div>
                  <div className="text-[#1C1C1E]">{cls.slot ? formatTime(cls.slot.start_time) : ''}</div>
                  <div className="text-[#8E8E93]">{cls.slot ? getTimezone() : ''}</div>
                  <div className="col-span-2 flex justify-end gap-2">
                    {cls.zoom_join_url ? (
                      <a
                        href={cls.zoom_join_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-[#5C2D91] hover:brightness-110 text-white rounded-xl font-medium transition-all"
                      >
                        <Video className="w-4 h-4" />
                        {t('Join')}
                      </a>
                    ) : (
                      <button
                        disabled
                        className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-black/[0.03] text-[#8E8E93] rounded-xl font-medium cursor-not-allowed"
                        title={t('Zoom link will be available closer to the lesson')}
                      >
                        <Clock className="w-4 h-4" />
                        {t('Join')}
                      </button>
                    )}
                    <button
                      onClick={() => setCancelConfirmId(cls.id)}
                      disabled={cancellingId === cls.id}
                      className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] border border-black/[0.08] hover:border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 text-[#636366] hover:text-[#FF3B30] rounded-xl font-medium transition-all disabled:opacity-50"
                    >
                      {cancellingId === cls.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <X className="w-4 h-4" />
                          {t('Cancel')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {bookingModal && (
        <BookingModal
          isOpen={!!bookingModal}
          onClose={() => setBookingModal(null)}
          onSuccess={() => {
            setBookingModal(null);
            loadClasses();
          }}
          childId={bookingModal.childId}
          childName={bookingModal.childName}
          mode={bookingModal.mode}
          existingBookingId={bookingModal.existingBookingId}
          existingSlotId={bookingModal.existingSlotId}
        />
      )}

      <ConfirmDialog
        isOpen={!!cancelConfirmId}
        onClose={() => setCancelConfirmId(null)}
        onConfirm={() => cancelConfirmId && handleCancelConfirm(cancelConfirmId)}
        title={t('Cancel Lesson')}
        message={t('Are you sure you want to cancel this lesson?')}
        confirmLabel={t('Cancel Lesson')}
        variant="danger"
        loading={!!cancellingId}
      />
    </div>
  );
};

export default SchedulePage;
